import { useState, useMemo } from "react";
import { buildHouseholds, householdMatches, describeSession } from "../utils/householdGroups";
import { normalizeHouseholdId } from "../utils/households";
import { TIER_COLORS } from "../utils/tiers";

// Families, and what their week at the club looks like.
//
// Household ids come from the club's member import file. Edits made here are
// stamped straight onto the players, but the next import re-stamps everyone it
// can match — so the banner below says so rather than letting a merge quietly
// come undone a month later.
export default function HouseholdsView({ players, groups, updatePlayer, onPlayerClick, onNotify }) {
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all");    // all | families | singles | unlinked
  const [needsPlacing, setNeedsPlacing] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [moving, setMoving] = useState(null);   // member being moved
  const [merging, setMerging] = useState(null); // household being merged

  const { families, singles, unlinked, allHouseholds } = useMemo(
    () => buildHouseholds(players, groups),
    [players, groups]
  );

  // A household nobody can be reached at — no member has an address. Distinct
  // from a member with no email of their own, who is still reachable through a
  // parent; that's the whole point of a household.
  const unreachable = h => h.emails.length === 0;

  const view = useMemo(() => {
    const q = search.trim().toLowerCase();
    const keepHousehold = h =>
      householdMatches(h, q) &&
      (!needsPlacing || h.unplaced > 0) &&
      (!noEmail || unreachable(h));
    // Unlinked people are individuals, so the same conditions read against the
    // person rather than a family.
    const keepMember = m =>
      (!q || String(m.player.name || "").toLowerCase().includes(q)) &&
      (!needsPlacing || m.sessions.length === 0) &&
      (!noEmail || !String(m.player.email || "").trim());

    return {
      families: scope === "all" || scope === "families" ? families.filter(keepHousehold) : [],
      singles: scope === "all" || scope === "singles" ? singles.filter(keepHousehold) : [],
      unlinked: scope === "all" || scope === "unlinked" ? unlinked.filter(keepMember) : [],
    };
  }, [families, singles, unlinked, search, scope, needsPlacing, noEmail]);

  // Chip counts describe the whole roster rather than the current filter, so
  // they stay a stable read of the club instead of shifting as you narrow.
  const needsPlacingCount =
    families.filter(h => h.unplaced > 0).length +
    singles.filter(h => h.unplaced > 0).length +
    unlinked.filter(m => m.sessions.length === 0).length;
  const noEmailCount =
    families.filter(unreachable).length +
    singles.filter(unreachable).length +
    unlinked.filter(m => !String(m.player.email || "").trim()).length;

  const SCOPES = [
    { key: "all", label: "All", count: families.length + singles.length + unlinked.length },
    { key: "families", label: "Families", count: families.length },
    { key: "singles", label: "Single-member", count: singles.length },
    { key: "unlinked", label: "Not linked", count: unlinked.length },
  ];

  const filtering = scope !== "all" || needsPlacing || noEmail || search.trim() !== "";
  const shownCount = view.families.length + view.singles.length + view.unlinked.length;

  function setHousehold(player, rawId, note) {
    const next = String(rawId ?? "").trim();
    updatePlayer(player.id, { householdId: next });
    setMoving(null);
    onNotify?.({ message: note || (next ? `${player.name} moved to household ${next}` : `${player.name} removed from their household`) });
  }

  // Merging rewrites every member of the source household, so it's one action
  // rather than moving people one at a time.
  function mergeInto(source, targetRawId) {
    const target = String(targetRawId ?? "").trim();
    if (!target || normalizeHouseholdId(target) === source.key) return;
    source.members.forEach(m => updatePlayer(m.player.id, { householdId: target }));
    setMerging(null);
    onNotify?.({
      message: `Merged ${source.members.length} member${source.members.length !== 1 ? "s" : ""} into household ${target}`,
    });
  }

  const totalFamilies = families.length;

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Households</h2>
          <p className="text-sm text-gray-500">
            {totalFamilies} famil{totalFamilies === 1 ? "y" : "ies"} · {singles.length} single-member ·{" "}
            {unlinked.length} not linked
          </p>
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search a family, member or email…"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          {SCOPES.map((s2, i) => (
            <button
              key={s2.key}
              onClick={() => setScope(s2.key)}
              className={`px-3 py-1.5 transition-colors ${i > 0 ? "border-l border-gray-300" : ""} ${
                scope === s2.key ? "bg-blue-600 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {s2.label} ({s2.count})
            </button>
          ))}
        </div>

        <button
          onClick={() => setNeedsPlacing(v => !v)}
          title="Households with at least one member who isn't in any session — and unlinked people with none"
          className={`text-sm px-3 py-1.5 rounded border transition-colors ${
            needsPlacing
              ? "bg-amber-500 border-amber-500 text-white"
              : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
          }`}
        >
          Needs placing ({needsPlacingCount})
        </button>

        <button
          onClick={() => setNoEmail(v => !v)}
          title="Households where nobody has an email address — the schedule mail-out can't reach them at all"
          className={`text-sm px-3 py-1.5 rounded border transition-colors ${
            noEmail
              ? "bg-red-500 border-red-500 text-white"
              : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
          }`}
        >
          ✉ No contact ({noEmailCount})
        </button>

        {filtering && (
          <button
            onClick={() => { setScope("all"); setNeedsPlacing(false); setNoEmail(false); setSearch(""); }}
            className="text-sm text-gray-500 hover:text-gray-800 px-2 py-1.5 transition-colors"
          >
            Clear filters
          </button>
        )}

        {filtering && (
          <span className="text-sm text-gray-400 ml-auto">
            {shownCount} shown
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
        Household ids come from the club's member import file. Changes made here stick,
        but uploading that file again re-links anyone it can match — so a merge may need doing twice.
      </p>

      {view.families.length === 0 && view.singles.length === 0 && view.unlinked.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">🏠</div>
          <p className="text-gray-500 text-sm">
            {filtering
              ? "Nothing matches those filters."
              : "No households yet — upload the club's member file from the Roster to link families."}
          </p>
        </div>
      )}

      {view.families.length > 0 && (
        <Section title="Families" count={view.families.length}>
          {view.families.map(h => (
            <HouseholdCard
              key={h.key}
              household={h}
              allHouseholds={allHouseholds}
              onPlayerClick={onPlayerClick}
              moving={moving}
              setMoving={setMoving}
              merging={merging}
              setMerging={setMerging}
              onSetHousehold={setHousehold}
              onMerge={mergeInto}
            />
          ))}
        </Section>
      )}

      {view.singles.length > 0 && (
        <Section title="Single-member households" count={view.singles.length}>
          {view.singles.map(h => (
            <HouseholdCard
              key={h.key}
              household={h}
              allHouseholds={allHouseholds}
              onPlayerClick={onPlayerClick}
              moving={moving}
              setMoving={setMoving}
              merging={merging}
              setMerging={setMerging}
              onSetHousehold={setHousehold}
              onMerge={mergeInto}
            />
          ))}
        </Section>
      )}

      {view.unlinked.length > 0 && (
        <Section title="Not linked to a household" count={view.unlinked.length}>
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
            {view.unlinked.map(({ player, sessions }) => (
              <div key={player.id} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                <button
                  onClick={() => onPlayerClick?.(player)}
                  className="font-medium text-gray-800 hover:text-blue-600 transition-colors"
                >
                  {player.name}
                </button>
                <span className="text-xs text-gray-400">{player.tier}</span>
                <span className="text-sm text-gray-500 flex-1 min-w-0">
                  {sessions.length === 0
                    ? <span className="italic text-gray-400">not in a session</span>
                    : sessions.map(describeSession).join("  ·  ")}
                </span>
                <HouseholdPicker
                  label="Link to…"
                  households={allHouseholds}
                  onPick={raw => setHousehold(player, raw)}
                />
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
        {title} <span className="text-gray-300">({count})</span>
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function HouseholdCard({
  household, allHouseholds, onPlayerClick,
  moving, setMoving, merging, setMerging, onSetHousehold, onMerge,
}) {
  const h = household;
  const isMerging = merging === h.key;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-gray-800">
              {h.name ? `The ${h.name} family` : `Household ${h.rawId}`}
            </span>
            <span className="text-xs font-mono text-gray-400">#{h.rawId}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {h.members.length} member{h.members.length !== 1 ? "s" : ""} ·{" "}
            {h.sessionCount} session{h.sessionCount !== 1 ? "s" : ""} a week
            {h.unplaced > 0 && (
              <span className="text-amber-600 font-medium"> · {h.unplaced} not placed</span>
            )}
          </div>
          {h.emails.length > 0 && (
            <div className="text-xs text-gray-400 mt-0.5 break-all">{h.emails.join(" · ")}</div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isMerging ? (
            <HouseholdPicker
              label="Merge into…"
              households={allHouseholds.filter(o => o.key !== h.key)}
              autoFocus
              onPick={raw => onMerge(h, raw)}
              onCancel={() => setMerging(null)}
            />
          ) : (
            <button
              onClick={() => setMerging(h.key)}
              title="Move everyone in this household into another one"
              className="text-xs border border-gray-300 hover:bg-gray-50 text-gray-600 px-2 py-1 rounded transition-colors"
            >
              Merge into…
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {h.members.map(({ player, sessions }) => {
          const colors = TIER_COLORS[player.tier] || TIER_COLORS["No Group"];
          const isMoving = moving === player.id;
          return (
            <div key={player.id} className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <button
                onClick={() => onPlayerClick?.(player)}
                className="font-medium text-gray-800 hover:text-blue-600 transition-colors"
              >
                {player.name}
              </button>
              <span className={`text-[11px] px-1.5 py-0.5 rounded ${colors.bg} ${colors.border} border text-gray-600`}>
                {player.tier}
              </span>
              {player.age != null && <span className="text-xs text-gray-400">age {player.age}</span>}

              <span className="text-sm text-gray-600 flex-1 min-w-0">
                {sessions.length === 0 ? (
                  <span className="italic text-gray-400">not in a session</span>
                ) : (
                  sessions.map(s => (
                    <span key={s.id} className="mr-3 whitespace-nowrap">
                      <span className="font-mono text-xs text-gray-500">{s.id}</span>{" "}
                      {describeSession(s)}
                    </span>
                  ))
                )}
              </span>

              {isMoving ? (
                <HouseholdPicker
                  label="Move to…"
                  households={allHouseholds}
                  autoFocus
                  allowClear
                  onPick={raw => onSetHousehold(player, raw)}
                  onCancel={() => setMoving(null)}
                />
              ) : (
                <button
                  onClick={() => setMoving(player.id)}
                  className="text-xs text-gray-400 hover:text-blue-600 transition-colors"
                >
                  Move to…
                </button>
              )}
            </div>
          );
        })}
      </div>

      {h.days.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-1">
          <span className="font-medium text-gray-600">At the club:</span>
          {h.days.map(d => (
            <span key={d.day}>
              {d.day}
              {d.count > 1 && <span className="text-gray-400"> ×{d.count}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Pick an existing household, or type a new membership number. Typing wins if
// both are filled, so a family that isn't on the roster yet can still be made.
function HouseholdPicker({ label, households, onPick, onCancel, autoFocus = false, allowClear = false }) {
  const [typed, setTyped] = useState("");
  const [open, setOpen] = useState(autoFocus);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs border border-gray-300 hover:bg-gray-50 text-gray-600 px-2 py-1 rounded transition-colors"
      >
        {label}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <select
        defaultValue=""
        onChange={e => { if (e.target.value !== "") onPick(e.target.value); }}
        className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-[11rem]"
      >
        <option value="">{label}</option>
        {households.map(o => (
          <option key={o.key} value={o.rawId}>
            #{o.rawId}{o.name ? ` · ${o.name}` : ""} ({o.members.length})
          </option>
        ))}
      </select>
      <input
        type="text"
        value={typed}
        onChange={e => setTyped(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" && typed.trim()) onPick(typed.trim());
          if (e.key === "Escape") { setOpen(false); onCancel?.(); }
        }}
        placeholder="or new #"
        className="border border-gray-300 rounded px-2 py-1 text-xs w-20 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      {allowClear && (
        <button
          onClick={() => onPick("")}
          title="Remove this member from their household"
          className="text-xs text-gray-400 hover:text-red-600 transition-colors px-1"
        >
          unlink
        </button>
      )}
      <button
        onClick={() => { setOpen(false); onCancel?.(); }}
        className="text-xs text-gray-400 hover:text-gray-600 px-1"
      >
        ×
      </button>
    </div>
  );
}
